import { useEffect, useState } from 'react';
import { ErrorState } from '../../../../components/ErrorState';
import { FolderTabs } from '../../../../components/FolderTabs';
import { Skeleton } from '../../../../components/Skeleton';
import { ApiError } from '../../../../lib/apiError';
import { ResponseAnswers } from '../../../learning-development/survey/components/SurveyResponseViewModal';
import { mySurveysApi } from '../mySurveysApi';
import { MySurveyTrainingRecommendationsTable } from './MySurveyTrainingRecommendationsTable';
import type { MySurveyAnswers } from '../mySurveyTypes';

type PreviewKey = 'survey' | 'trainings';

/**
 * Self Service › Survey › Preview — a second strip of folder tabs, the Survey profile's Responses
 * frame exactly (product call, 12 Oct 2026): **Survey**, the employee's OWN answers in words;
 * **Training Recommendations**, the APPROVED trainings on the plan they belong to (product call,
 * 13 Oct 2026 — it replaced the plan's own sub-tab), in the STANDARD table whose title opens that
 * recommendation's own profile. There is NO Report sub-tab (product call, 13 Oct 2026 — removed): a
 * training's report is submitted, read and edited on that recommendation's profile, under Report.
 *
 * This REVERSES "the plan never renders in Preview": the plan now reads here as well as in the
 * banner's popup, which stays the place it is ACCEPTED or DECLINED.
 *
 * Each sub-tab fetches when it first OPENS. A tab with nothing to show yet SAYS so, in the state's
 * own words, rather than vanishing — a strip that changes length per record is two different pages.
 *
 * **Whether there is anything to show is the SURVEY's fact (`answered`), never read off a 404** — a 404 also comes back from an API that predates an
 * endpoint. Every failure is an error, with its reference.
 */
export function MySurveyPreviewPanel({
  surveyId,
  answered,
  readOnly = false,
}: {
  surveyId: string;
  answered: boolean;
  /** The survey is deleted: the recommendations' tracks stay readable, and nothing is offered on them. */
  readOnly?: boolean;
}) {
  const [view, setView] = useState<PreviewKey>('survey');

  return (
    // The tab is `bleed`, so the inset is this wrapper's — as on the Survey profile's own tabs.
    <div className="flex min-w-0 flex-col p-4">
      <FolderTabs<PreviewKey>
        id="my-survey-preview"
        ariaLabel="Preview"
        paddingX="px-3"
        activeKey={view}
        onChange={setView}
        tabs={[
          { key: 'survey', label: 'Survey' },
          // The APPROVED trainings on the plan the reader belongs to — product call, 13 Oct 2026.
          { key: 'trainings', label: 'Training Recommendations' },
        ]}
      />

      {/* All four borders, as Responses' — the active tab's white edge breaks the top. */}
      <div className="rounded-b-panel border border-slate-300 bg-white p-4">
        {/*
          The SAME centred column Learning & Development's Preview reads in (product call, 12 Oct
          2026: "it should be same") — `max-w-4xl`, centred, the cap every card column takes. Full
          width, the answers stretched to the panel's edges and the two Previews disagreed.
        */}
        {view === 'trainings' ? (
          // A TABLE takes the panel's whole width — the centred column is for reading, not for columns.
          <MySurveyTrainingRecommendationsTable surveyId={surveyId} answered={answered} readOnly={readOnly} />
        ) : (
          <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
            <SurveyView surveyId={surveyId} answered={answered} />
          </div>
        )}
      </div>
    </div>
  );
}

function Nothing({ children }: { children: string }) {
  return <p className="text-sm text-slate-500">{children}</p>;
}

function Loading() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

/** One read, made when the sub-tab opens, with its retry — `enabled` false makes none. */
function useRead<T>(enabled: boolean, read: (signal: AbortSignal) => Promise<T>, failure: string, key: string) {
  const [value, setValue] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();

    read(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setValue(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(caught instanceof ApiError ? caught : new ApiError(failure, 0));
      });

    return () => controller.abort();
    // `read` is rebuilt every render; `key` is what it actually depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, attempt]);

  return {
    value,
    error,
    retry: () => {
      setError(null);
      setAttempt((a) => a + 1);
    },
  };
}

/** The employee's OWN answers — through the administrator's `<ResponseAnswers>`, so one answer never reads two ways. */
function SurveyView({ surveyId, answered }: { surveyId: string; answered: boolean }) {
  const { value, error, retry } = useRead<MySurveyAnswers>(
    answered,
    (signal) => mySurveysApi.myAnswers(surveyId, signal),
    'Could not load your answers.',
    surveyId,
  );

  if (!answered) return <Nothing>You have not answered this survey yet. Your answers appear here once you have.</Nothing>;
  if (error) return <ErrorState message={error.message} correlationId={error.correlationId} onRetry={retry} />;
  if (!value) return <Loading />;

  return (
    // `<ResponseAnswers>` emits its blocks as SIBLINGS and leaves the gap to its container —
    // without this wrapper the heading, the question cards and Remarks touch.
    <div className="flex flex-col gap-4">
      <ResponseAnswers
        // The response itself is always live here — a deleted SURVEY is the banner's to say.
        view={{ ...value, isActive: true }}
        respondentName={value.myName ?? 'You'}
      />
    </div>
  );
}

/**
 * The report on the completed training — its facts, the attachment, then the report itself. It
 * arrives WITH the page's own read (`training`), so this view fetches nothing but the file, and
 * that only when asked: a FETCH then an object URL, never a link at the endpoint.
 */
